import { Spinner } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getLicenseDetailOptions, getLicenseStatusOptions } from "@/api";
import { Text } from "@/components/Text";
import { usePermissions } from "@/features/account/useCurrentUser";
import { AdminPage } from "../AdminPage";
import { useAdminCanEdit } from "../useAdminCanEdit";
import { KillSwitch } from "./KillSwitch";
import { LicenseConfigSection } from "./LicenseConfigSection";
import { LicenseStatusSection } from "./LicenseStatusSection";

/**
 * /admin/license. Every platform admin sees the status; install admins (the
 * single check below) also get the configuration and the kill switch.
 */
export function LicensePage() {
  const { t } = useTranslation("adminLicense");
  const canEdit = useAdminCanEdit();
  const { isInstallAdmin } = usePermissions();

  const status = useQuery(getLicenseStatusOptions());
  const detail = useQuery({ ...getLicenseDetailOptions(), enabled: isInstallAdmin });
  const license = isInstallAdmin ? detail.data?.data : undefined;

  let statusBody = null;
  if (status.isPending) statusBody = <Spinner />;
  else if (status.isError) {
    statusBody = (
      <Text tone="error" role="alert">
        {t("loadFailed")}
      </Text>
    );
  } else {
    statusBody = <LicenseStatusSection status={status.data.data} detail={license} />;
  }

  return (
    <AdminPage title={t("title")} subtitle={t("subtitle")}>
      <div className="flex flex-col gap-10">
        {statusBody}
        {isInstallAdmin && <LicenseConfigSection detail={detail} canEdit={canEdit} />}
        {isInstallAdmin && canEdit && license && <KillSwitch license={license} />}
      </div>
    </AdminPage>
  );
}
